import * as React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import LocationOnIcon from '@mui/icons-material/LocationOn';

import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import ChecklistIcon from '@mui/icons-material/Checklist';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { isMobile, isTablet } from 'react-device-detect';
import {
  IDeliveryBatch,
  IOrderDeliveryTask,
} from '../../../interfaces/delivery-company/tasks';
import { useImmer } from 'use-immer';
import { format } from 'date-fns';
import {
  NotificationTemplate,
  OrderType,
  OrganisationSettings,
} from '../../../interfaces/delivery-company/orders';
import { timestampedMessage } from '../../common/multi-package-scanner/multi-package-scanner';
import { AuthContext } from '../../../contexts/user-context/user-context';
import axios, { AxiosResponse } from 'axios';
import { API_ENDPOINT } from '../../../configurations/global.config';
import MobileCardList from '../../common/mobile-card-list/mobile-card-list';
import { green, grey, red } from '@mui/material/colors';
import SnackBar from '../../common/snack-bar/snack-bar';
import SendMessageDialog from '../common/send-message-dialog/send-message-dialog';
import { QrCode } from '@mui/icons-material';
import {
  DeliveryBatchState,
  OrderDeliveryTaskState,
} from '../../../enums/tasks';
import {
  AWAITING_COLLECTION,
  FINISHED,
  IN_PROGRESS,
  PENDING,
  TASK_STATE_CATEGORY,
} from './filter_utils';
import AddNotesDialog from '../common/add-notes-dialog';
import DeliveryBatchDialog from './scan-delivery-batch-dialog';
import GavelIcon from '@mui/icons-material/Gavel';
import { DeliveryState, Workflow } from '../../../enums/orders';
import TaskDialog from './scan-task-dialog';
import { ListType } from './driver-delivery-tasks';
import ScanDeliveryBatchDialog from './scan-delivery-batch';
import { IconWithText } from '../../common/styles/components';
import DriverResponsiveOrdersListing from '../common/delivery-order-listing';
import { SortingByColumnState } from '../../common/generic-table/utils';
import {
  getDeliveryBatchStateColor,
  getDeliveryBatchStateIcon,
} from '../../common/styles/delivery-batch.styles';
import MarkAsFailedDialog from '../common/mark-as-failed-dialog';
import DeliveryBatchQuickScanDialog from '../quick-scan-dialogs/delivery-batch-dialog';
import { buildResolveTemplateArgsForDelivery } from '../../delivery-company-components/settings/notification-manager/utils';

interface DeliveryCardListingProps {
  isLoading: boolean;
  listType: ListType;
  batches: IDeliveryBatch[];
  tasks: IOrderDeliveryTask[];
  statusFilter: TASK_STATE_CATEGORY;
  reloadList: () => void;
  totalCount: number;
  updateTasks: (
    mutator: (task: IOrderDeliveryTask) => IOrderDeliveryTask
  ) => void;
  changeStateCategory: (s: TASK_STATE_CATEGORY) => void;
  loadMoreTasks: () => void;
}
export default function DeliveryCardListing({
  listType,
  isLoading,
  batches,
  tasks,
  statusFilter,
  reloadList,
  totalCount,
  changeStateCategory,
  loadMoreTasks,
  updateTasks,
}: DeliveryCardListingProps) {
  const [selectedBatches, updateSelectedBatches] = useImmer<
    Map<string, IDeliveryBatch>
  >(new Map());

  const [selectedOrders, updateSelectedOrders] = useImmer<
    Map<string, OrderType>
  >(new Map());

  const [selectedTasks, updateSelectedTasks] = useImmer<
    Map<string, IOrderDeliveryTask>
  >(new Map());

  const [
    qrScannerDialogOpenForDeliveryBatch,
    setQrScannerDialogOpenForDeliveryBatch,
  ] = React.useState<IDeliveryBatch | null>(null);
  const [qrScannerDialogOpenForTask, setQrScannerDialogOpenForTask] =
    React.useState<IOrderDeliveryTask | null>(null);

  const [qrScannerDialogOpen, setQrScannerDialogOpen] = React.useState(false);
  const [whatsAppDialogOpen, setWhatsAppDialogOpen] = React.useState(false);
  const totalLoadedBatches = batches.length;
  const totalLoadedTasks = tasks.length;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [notificationTemplates, setNotificationTemplates] =
    React.useState<NotificationTemplate[]>();
  const [addNoteDialogOpen, setAddNoteDialogOpen] = React.useState(false);
  const [markAsFailedDialogOpen, setMarkAsFailedDialogOpen] =
    React.useState(false);
  const [snackBar, setSnackBar] = React.useState<timestampedMessage>();
  const { user, config } = React.useContext(AuthContext);
  const [response, setResponse] = React.useState<AxiosResponse>();

  const [sorting, setSorting] = React.useState<SortingByColumnState>({
    column: 'creation_date',
    order: 'decreasing',
  });
  const handleAddNoteMenuClick = () => {
    setAddNoteDialogOpen(true);
    handleMenuClose();
  };

  const handleMarkAsFailedMenuClick = () => {
    setMarkAsFailedDialogOpen(true);
    handleMenuClose();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleSendWhatAppMessage = async (template: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/delivery-tasks/notify/`;
    const requestData = {
      message: template,
      task_ids: Array.from(selectedTasks.values()).map((task) => task.id),
    };

    return axios.post(endpoint, requestData, config);
  };

  const handleStartTask = async (id: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/delivery-task/${id}/`;
    const requestData = { status: OrderDeliveryTaskState.IN_PROGRESS };

    try {
      await axios.patch(endpoint, requestData, config);
      updateTasks((t) =>
        t.id == id ? { ...t, status: OrderDeliveryTaskState.IN_PROGRESS } : t
      );
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleStartTaskMenuClick = async () => {
    const endpoint = `${API_ENDPOINT}/api/v1/delivery-tasks/bulk-update/`;
    const requestData = Array.from(selectedTasks.values()).map((t) => {
      return {
        id: t.id,
        status: OrderDeliveryTaskState.IN_PROGRESS,
      };
    });

    try {
      const response = await axios.patch(endpoint, requestData, config);

      if (response?.status === 200) {
        const newValue = {
          timestamp: Date.now(),
          content: 'Task Started Successfully',
          level: 'success',
        };
        setSnackBar(newValue as timestampedMessage);
      } else {
        const newValue = {
          timestamp: Date.now(),
          content: 'Could not Start Task',
          level: 'error',
        };
        setSnackBar(newValue as timestampedMessage);
      }
    } catch (error) {
      console.error('Error processing tasks:', error);
    }
    handleMenuClose();
    reloadList();
    updateSelectedBatches((prev) => prev.clear());
    updateSelectedTasks((prev) => prev.clear());
  };

  React.useEffect(() => {
    updateSelectedBatches((prev) => prev.clear());
    updateSelectedTasks((prev) => prev.clear());
  }, [statusFilter]);

  const handleAddNote = async (template: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/delivery-tasks/bulk-update/`;

    const requestData = Array.from(selectedTasks.values()).map((t) => {
      return {
        id: t.id,
        notes: [template],
      };
    });
    return axios.patch(endpoint, requestData, config);
  };
  const handleMarkAsFailedTasks = async (template: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/delivery-tasks/bulk-update/`;

    const requestData = Array.from(selectedTasks.values()).map((t) => {
      return {
        id: t.id,
        failure_reason: template,
        status: OrderDeliveryTaskState.AWAITING_RETURN,
      };
    });
    return axios.patch(endpoint, requestData, config);
  };

  const handleWhatsAppMenuItemClick = (action: string) => {
    setWhatsAppDialogOpen(true);
    handleMenuClose();
  };

  React.useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const endpoint = `${API_ENDPOINT}/api/v1/settings`;
        const response = await axios.get<OrganisationSettings>(
          endpoint,
          config
        );
        setNotificationTemplates(response.data.notification_templates);
      } catch (e) {
        setSnackBar({
          timestamp: Date.now(),
          content: 'Failed to load notification templates',
          level: 'error',
        });
      }
    };

    fetchTemplates();
  }, []);

  return (
    <Box sx={{ padding: 0 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 10,
          marginLeft: 10,
        }}
      >
        <Chip
          icon={<ChecklistIcon />}
          color="primary"
          label={
            !(statusFilter == FINISHED || statusFilter == AWAITING_COLLECTION)
              ? `Selected ${selectedTasks.size}/${totalCount}`
              : `Total ${totalCount}`
          }
          variant={
            selectedTasks.size === totalLoadedTasks && selectedTasks.size > 0
              ? 'filled'
              : 'outlined'
          }
        />
        <div>
          {(statusFilter == PENDING || statusFilter == IN_PROGRESS) && (
            <Button
              size={(isMobile && !isTablet) ? 'small' : 'medium'}
              variant="outlined"
              aria-controls="actions-menu"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              disabled={selectedBatches.size === 0 && selectedTasks.size === 0}
            >
              Actions
            </Button>
          )}
          <Menu
            id="actions-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem
              onClick={() => handleWhatsAppMenuItemClick('Send Message')}
            >
              Send Message{' '}
              <WhatsAppIcon fontSize="small" style={{ marginLeft: '8px' }} />
            </MenuItem>
            <MenuItem onClick={() => handleAddNoteMenuClick()}>
              Add Note
            </MenuItem>
            <MenuItem onClick={() => handleMarkAsFailedMenuClick()}>
              Mark As Failed
            </MenuItem>
            {statusFilter == PENDING && (
              <MenuItem onClick={() => handleStartTaskMenuClick()}>
                Start Task{' '}
                <GavelIcon fontSize="small" style={{ marginLeft: '8px' }} />
              </MenuItem>
            )}
            {statusFilter == IN_PROGRESS &&
              Array.from(selectedTasks.values()).some(
                (task) => task.status === OrderDeliveryTaskState.AWAITING_RETURN
              ) && (
                <MenuItem onClick={() => handleStartTaskMenuClick()}>
                  Resume Task{' '}
                  <GavelIcon fontSize="small" style={{ marginLeft: '8px' }} />
                </MenuItem>
              )}
          </Menu>
        </div>
      </div>

      {statusFilter == AWAITING_COLLECTION && (
        <div style={{ cursor: 'pointer', textAlign: 'center' }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => setQrScannerDialogOpen(true)}
          >
            <QrCode fontSize="small" sx={{ mr: 1 }} /> Collect Packages
          </Button>{' '}
          <br />
          <br />
        </div>
      )}

      {listType == ListType.Batch && (
        <MobileCardList<IDeliveryBatch>
          items={batches}
          rowConfigs={[
            {
              display_row: (batch) => <b> Delivery Batch #{batch.id}</b>,
            },
            {
              display_row: (batch) => {
                return (
                  <>
                    {format(new Date(batch.creation_date ?? ''), 'dd-MM-yyyy')}
                  </>
                );
              },
            },
            {
              display_row: (batch) => (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: '#f0f0f0', // Light gray background
                    borderRadius: '12px', // Rounded corners
                    padding: '4px 8px', // Padding around the text
                  }}
                >
                  {batch.package_count} items
                </Box>
              ),
            },
            {
              display_row: (batch) => (
                <IconWithText>
                  <LocationOnIcon color="primary" /> {batch.branch?.directions}{' '}
                </IconWithText>
              ),
            },
            {
              display_row: (batch) => (
                <div style={{ color: red[400] }}>
                  {(batch.notes ?? []).join(' | ')}
                </div>
              ),
            },
          ]}
          isLoading={isLoading}
          selectedItems={selectedBatches}
          updateSelectedItems={updateSelectedBatches}
          getItemUID={(batch) => batch.id}
          selectionEnabled={false}
          cardFooter={(batch) => {
            return {
              content: (
                <IconWithText centered>
                  {getDeliveryBatchStateIcon(batch.status)}
                  <>{batch.status.replaceAll('_', ' ')}</>
                </IconWithText>
              ),
              background: getDeliveryBatchStateColor(batch.status),
            };
          }}
          onCardClick={(batch) => {
            // most probably canceled and finished too
            setQrScannerDialogOpenForDeliveryBatch(batch);
          }}
        />
      )}

      {listType == ListType.Task && (
        <DriverResponsiveOrdersListing
          statusFilter={statusFilter}
          taskList={tasks}
          enableSelection={statusFilter == FINISHED ? false : true}
          selectedTasks={selectedTasks}
          updateSelectedTasks={updateSelectedTasks}
          isLoading={isLoading}
          sorting={sorting}
          setSorting={setSorting}
          onCardClick={(task) => {
            // most probably canceled and finished too
            if (task.status != OrderDeliveryTaskState.COMPLETED)
              setQrScannerDialogOpenForTask(task);
          }}
        />
      )}
      {qrScannerDialogOpenForTask && (
        <TaskDialog
          reloadTasks={reloadList}
          task={qrScannerDialogOpenForTask}
          setSnackBarResult={(t?: timestampedMessage) => setSnackBar(t)}
          onClose={() => {
            setQrScannerDialogOpenForTask(null);
            reloadList();
          }}
        />
      )}
      {qrScannerDialogOpenForDeliveryBatch && (
        <DeliveryBatchQuickScanDialog
          id={qrScannerDialogOpenForDeliveryBatch.id}
          onClose={() => {
            setQrScannerDialogOpenForDeliveryBatch(null);
            reloadList();
          }}
        />
      )}

      {whatsAppDialogOpen && (
        <SendMessageDialog
          setSnackBarResult={(t?: timestampedMessage) => setSnackBar(t)}
          onSubmit={handleSendWhatAppMessage}
          onClose={() => setWhatsAppDialogOpen(false)}
          stage="Delivery"
          onSuccess={() => {
            if (statusFilter == PENDING) {
              handleStartTaskMenuClick();
              changeStateCategory(IN_PROGRESS as TASK_STATE_CATEGORY);
            }
          }}
          startTask={handleStartTask}
          locallyResolvedTargets={Array.from(selectedTasks.values()).map(
            (task) => {
              return {
                id: task.id,
                label: `Order #${task.order.order_id}`,
                phone_number:
                  task.order.workflow == Workflow.STANDARD
                    ? task.order.customer?.phone_number ?? ''
                    : task.order.merchant_location?.contact_phone ?? '',
                resolveTemplateArgs: buildResolveTemplateArgsForDelivery(
                  task.order,
                  user?.name ?? '',
                  user?.organisation?.org_id ?? 'ERROR'
                ),
                disabled: task.order.workflow == Workflow.STANDARD && task.order.customer?.notifications_disabled,
                alreadyStarted:
                  task.status != OrderDeliveryTaskState.OUT_FOR_DELIVERY,
              };
            }
          )}
        />
      )}
      {addNoteDialogOpen && (
        <AddNotesDialog
          onSuccess={(m: string) =>
            updateTasks((p) =>
              selectedTasks.has(p.id)
                ? { ...p, notes: [...(p.notes ?? []), m] }
                : p
            )
          }
          setSnackBarResult={(t?: timestampedMessage) => setSnackBar(t)}
          onSubmit={handleAddNote}
          onClose={() => setAddNoteDialogOpen(false)}
        />
      )}
      {markAsFailedDialogOpen && (
        <MarkAsFailedDialog
          failure_reasons={[
            'Shop closed',
            'Wrong amount',
            'Wrong invoice',
            'Person absent',
            'Invoice lost',
          ]}
          onSuccess={(r) => reloadList()}
          setSnackBarResult={(t?: timestampedMessage) => setSnackBar(t)}
          onSubmit={handleMarkAsFailedTasks}
          onClose={() => setMarkAsFailedDialogOpen(false)}
        />
      )}
      {qrScannerDialogOpen && (
        <ScanDeliveryBatchDialog
          tasks={batches}
          setSnackBarResult={(t?: timestampedMessage) => setSnackBar(t)}
          onSubmit={(batch) => {
            setQrScannerDialogOpenForDeliveryBatch(batch);
          }}
          onClose={() => setQrScannerDialogOpen(false)}
        />
      )}

      {(listType == ListType.Task
        ? totalLoadedTasks < totalCount
        : totalLoadedBatches < totalCount) && (
        <Button
          fullWidth
          variant="outlined"
          onClick={() => loadMoreTasks()}
          sx={{ marginTop: 2 }}
        >
          See More
        </Button>
      )}

      {snackBar && snackBar.content && snackBar.content !== '' && (
        <SnackBar
          message={snackBar.content}
          level={snackBar.level}
          key={snackBar.timestamp}
        />
      )}
    </Box>
  );
}
